import React, { useState, useEffect, useRef } from 'react';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';

import ResetPassword from './ResetPassword';

// 🔍 GLOBAL FETCH DEBUG
const originalFetch = window.fetch;

window.fetch = async (...args) => {
  console.log('FETCH CALL →', args);

  try {
    const res = await originalFetch(...args);
    return res;
  } catch (err) {
    console.error('FETCH FAILED →', args, err);
    throw err;
  }
};

function App() {

  const API = 'https://sales-app-backend-production.up.railway.app';

  const [opportunities, setOpportunities] = useState([]);

  const [accounts, setAccounts] = useState([]);

  const [owners, setOwners] = useState([]);

  const [lobFilter, setLobFilter] = useState('all');

  const [ownerFilter, setOwnerFilter] = useState('all');

  const [view, setView] = useState('pipeline');

  const [openNotes, setOpenNotes] = useState({});

  const [lobs, setLobs] = useState([]);

  const [draggingId, setDraggingId] = useState(null);

  const [dragOverStage, setDragOverStage] = useState(null);

  const [editingOwnerId, setEditingOwnerId] = useState(null);

  const [editingLobId, setEditingLobId] = useState(null);

  const [clientFilter, setClientFilter] = useState('all');

  const [openCards, setOpenCards] = useState({});

  const [hoveredCard, setHoveredCard] = useState(null);

  const [hoverTimeout, setHoverTimeout] = useState(null);

  const [editingValueId, setEditingValueId] = useState(null);

  const [actionFilter, setActionFilter] = useState('all');

  const [editingProbabilityId, setEditingProbabilityId] = useState(null);

  const [viewMode, setViewMode] = useState('kanban');

  const [funnelMode, setFunnelMode] = useState('value');

  const [openRows, setOpenRows] = useState({});

  const pipelineRef = useRef(null);

  const setupRef = useRef(null);

  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  const [stageTiming, setStageTiming] = useState([]);

  const [user, setUser] = useState(() => {
  const stored = localStorage.getItem('user');
  return stored ? JSON.parse(stored) : null;
});

  const [isSliding, setIsSliding] = useState(false);

  const isAdmin = user?.role === 'admin';

  const canDrag = (op) =>
    isAdmin || !['won', 'lost'].includes(op.status);

  // 🔒 Admin-only (structure changes)
  const canDelete = () => isAdmin;
  const canEditOwner = () => isAdmin;
  const canEditLob = () => isAdmin;

  // ✏️ Allowed for all users (sales activity)
  const canEditValue = () => true;
  const canEditProbability = () => true;
  const canAddActivity = () => true;

  const [form, setForm] = useState({

    account_id: '',

    owner_ref: '',

    lob_id: '',

    name: ''

  });

  const login = async (email, password, remember) => {
    const res = await fetch(`${API}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (res.ok) {
      const data = await res.json();

      setUser(data);

      localStorage.setItem('user', JSON.stringify(data));

      const storage = remember ? localStorage : sessionStorage;
      storage.setItem('user', JSON.stringify(data));

    } else {
      throw new Error('Login failed');
    }
  };

const fetchOpportunities = () => {
  const url = `${API}/opportunities`;

  console.log('URL →', url);

  try {
    const u = new URL(url);   // 👈 force validation
    console.log('VALID URL →', u.href);
  } catch (e) {
    console.error('INVALID URL →', url, e);
    return;
  }

  fetch(url)
  .then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      console.error('API ERROR /opportunities →', res.status, text);
      throw new Error('API failed');
    }
    return res.json();
  })
  .then(setOpportunities)
  .catch(err => console.error('FETCH ERROR:', err));
};

  const fetchAccounts = () => {

    fetch(`${API}/accounts`)
  .then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      console.error('API ERROR /accounts →', res.status, text);
      throw new Error('API failed');
    }
    return res.json();
  })
  .then(setAccounts)
  .catch(console.error);

  };

  const fetchOwners = () => {

    fetch(`${API}/owners`)
  .then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      console.error('API ERROR /owners →', res.status, text);
      throw new Error('API failed');
    }
    return res.json();
  })
  .then(setOwners)
  .catch(console.error);

  };

  const fetchLobs = () => {

    fetch(`${API}/lob`)
  .then(async (res) => {
    if (!res.ok) {
      const text = await res.text();
      console.error('API ERROR /lob →', res.status, text);
      throw new Error('API failed');
    }
    return res.json();
  })
  .then(setLobs)
  .catch(console.error);

  };

  const fetchStageTiming = () => {
  fetch(`${API}/stage-duration`)

  .then(async (res) => {

    const text = await res.text();

    if (!res.ok) {

      console.error('API ERROR /stage-duration →', res.status, text);

      throw new Error('API failed');

    }

    console.log('RAW stage-duration →', text);

      try {
        const data = JSON.parse(text);

        const clean = data.map(s => ({
          stage: s.stage,
          avg_days: Number(s.avg_days) || 0   // 👈 force safe number
        }));

        setStageTiming(clean);

      } catch (err) {
        console.error('PARSE ERROR stage-duration:', err);
      }
    })
    .catch(err => console.error('FETCH ERROR stage-duration:', err));
};

  useEffect(() => {
    const savedUser =
      localStorage.getItem('user') ||
      sessionStorage.getItem('user');

    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {

    fetchOpportunities();

    fetchAccounts();

    fetchOwners();

    fetchLobs();

    fetchStageTiming();

  }, []);

  useEffect(() => {

    const link = document.createElement('link');

    link.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined';

    link.rel = 'stylesheet';

    document.head.appendChild(link);

  }, []);

  useEffect(() => {

    const el = view === 'pipeline' ? pipelineRef.current : setupRef.current;

    if (el) {

      setIndicatorStyle({

        top: el.offsetTop,

        height: el.offsetHeight

      });

    }

  }, [view]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const createOpportunity = () => {

    if (!form.account_id || !form.owner_ref || !form.name.trim()) {

      alert('Complete all fields');

      return;

    }

    fetch(`${API}/opportunities`, {

      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({

        account_id: Number(form.account_id),

        owner_ref: Number(form.owner_ref),

        lob_id: form.lob_id ? Number(form.lob_id) : null,

        name: form.name,

        value: form.value ? Number(form.value) : 0

      })

    }).then(() => {

      fetchOpportunities();

      setForm({

        account_id: '',

        owner_ref: '',

        lob_id: '',

        name: '',

        value: ''

      });

    });

  };

  const filteredOpportunities = opportunities.filter(op => {
    const today = new Date();

    const isOverdue =
      !['won', 'lost'].includes(op.status) &&
      op.est_close_date &&
      new Date(op.est_close_date) < today;

    const referenceDate = op.last_activity || op.created_at;

    const isInactive =
      !['won', 'lost'].includes(op.status) &&
      !op.next_action_date &&
      (today - new Date(referenceDate)) / (1000 * 60 * 60 * 24) > 7;

    const isActionDue =
      op.next_action_date &&
      new Date(op.next_action_date) <= today;

    const actionMatch =
      actionFilter === 'all' ||
      (actionFilter === 'due' && isActionDue) ||
      (actionFilter === 'overdue' && isOverdue) ||
      (actionFilter === 'inactive' && isInactive);

    return (
      (ownerFilter === 'all' || op.owner === ownerFilter) &&
      (lobFilter === 'all' || op.line_of_business === lobFilter) &&
      (clientFilter === 'all' || op.client === clientFilter) &&
      actionMatch
    );
  });

  const path = window.location.pathname;

if (path.startsWith('/reset-password/')) {

  return <ResetPassword />;

}

  if (!user) {

    return (

      <div className="h-screen flex">

        {/* LEFT — LOGIN PANEL */}
        <div className="w-1/3 bg-[#016690] flex items-center justify-center">

          <div className="w-72 space-y-4">

            {/* LOGO */}
            <div className="flex items-center gap-2 mb-2">
              <img
                src="/LogoComplementCubeBlue.png"
                alt="Logo"
                className="w-8 h-8 object-contain"
              />
              <span className="text-[18px] font-semibold text-white">
                Cube <span className="text-[#ffb171]">Sales</span>
              </span>
            </div>

            {/* TAGLINE */}
            <p className="text-sm text-white leading-snug">
              Driving growth through smarter pipeline management and actionable insights.
            </p>

            {/* ERROR MESSAGE */}
            {form.loginError && (
              <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                {form.loginError}
              </div>
            )}

            {/* EMAIL */}
            <input
              id="email"
              placeholder="Email"
              className="border p-2 w-full rounded-lg"
            />

            {/* PASSWORD */}
            <input
              id="password"
              type="password"
              placeholder="Password"
              className="border p-2 w-full rounded-lg"
            />

            {/* REMEMBER + FORGOT */}
            <div className="flex items-center justify-between text-xs text-white">

              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.remember || false}
                  onChange={(e) =>
                    setForm({ ...form, remember: e.target.checked })
                  }
                />
                Remember me
              </label>

              <span
                onClick={() => {
                  const email = prompt('Enter your email to reset password:');
                  if (!email) return;

                  fetch(`${API}/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  })
                    .then(res => res.json())
                    .then(() => alert('If the email exists, a reset link was sent'))
                    .catch(() => alert('Error sending reset email'));
                }}
                className="cursor-pointer hover:text-[#05344c]"
              >
                Forgot password?
              </span>

            </div>

            {/* LOGIN BUTTON */}
            <button
              onClick={async () => {
                const email = document.getElementById('email').value;
                const password = document.getElementById('password').value;

                try {
                  await login(email, password, form.remember);

                  setForm({ ...form, loginError: '' });

                } catch (err) {
                  setForm({ ...form, loginError: 'Invalid credentials' });
                }
              }}
              className="bg-[#05344c] text-white w-full py-2 rounded-full hover:opacity-90"
            >
              Login
            </button>

            {/* POWERED BY */}
            <div className="text-[10px] text-gray-300 text-center pt-2">
              Powered by Complement IT SaaS
            </div>

          </div>

        </div>

        {/* RIGHT — IMAGE AREA */}
        <div className="w-2/3 relative">

          <img
            src="/bg.png"  // 🔁 replace anytime
            alt="Background"
            className="w-full h-full object-cover"
          />

          {/* Optional overlay for readability */}
          <div className="absolute inset-0 bg-black/10" />

        </div>

      </div>

    );

  }

  return (

    <div className="flex h-screen bg-[#f8f7f4]">

      {/* Sidebar */}
      <div className="relative w-64 bg-[#016690] text-white p-5">

        {/* Static rail */}
        <div className="absolute left-0 top-0 h-full w-[3px] bg-[#004d6d]" />

        {/* Dynamic indicator */}
        <div
          className="absolute left-0 w-[3px] bg-white rounded-r"
          style={{
            top: indicatorStyle.top,
            height: indicatorStyle.height,
            transition: 'all 400ms cubic-bezier(0.25, 1.1, 0.3, 1)'
          }}
        />

        <div className="flex items-center gap-2 mb-6">

          {/* Logo */}
          <img
            src="/LogoComplementCubeBlue.png"   // ← replace with your actual path
            alt="Logo"
            className="w-8 h-8 object-contain"
          />

          {/* Title */}
          <h1 className="text-[18px] font-semibold">Complement Group</h1>

        </div>

        <div className="space-y-3">

          {/* PIPELINE */}
          <div
            ref={pipelineRef}
            onClick={() => setView('pipeline')}
            className={`flex items-center gap-3 cursor-pointer pl-4 py-2 rounded-md transition-all duration-150
        ${view === 'pipeline'
                ? 'bg-[#075173] text-white'
                : 'text-[#b7eaff] hover:bg-[#075173]/60 hover:text-white'
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
              className="w-6 h-6 shrink-0"
              fill="currentColor"
            >
              <path d="M744-144q-41 0-72-24t-42-60H396q-55 0-93.5-38.5T264-360q0-55 38.5-93.5T396-492h120q35 0 59.5-24.5T600-576q0-35-24.5-59.5T516-660H330q-11 36-42 60t-72 24q-50 0-85-35t-35-85q0-50 35-85t85-35q41 0 72 24t42 60h186q65 0 110.5 45.5T672-576q0 65-45.5 110.5T516-420H396q-25 0-42.5 17.5T336-360q0 25 17.5 42.5T396-300h234q11-36 42-60t72-24q50 0 85 35t35 85q0 50-35 85t-85 35ZM250-662q14-14 14-34t-14-34q-14-14-34-14t-34 14q-14 14-14 34t14 34q14 14 34 14t34-14Z" />
            </svg>

            <span>Sales Pipeline</span>
          </div>

          {/* SETUP */}
          <div
            ref={setupRef}
            onClick={() => setView('setup')}
            className={`flex items-center gap-3 cursor-pointer pl-4 py-2 rounded-md transition-all duration-150
        ${view === 'setup'
                ? 'bg-[#075173] text-white'
                : 'text-[#b7eaff] hover:bg-[#075173]/60 hover:text-white'
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
              className="w-6 h-6 shrink-0"
              fill="currentColor"
            >
              <path d="m403-96-22-114q-23-9-44.5-21T296-259l-110 37-77-133 87-76q-2-12-3-24t-1-25q0-13 1-25t3-24l-87-76 77-133 110 37q19-16 40.5-28t44.5-21l22-114h154l22 114q23 9 44.5 21t40.5 28l110-37 77 133-87 76q2 12 3 24t1 25q0 13-1 25t-3 24l87 76-77 133-110-37q-19 16-40.5 28T579-210L557-96H403Zm77-240q60 0 102-42t42-102q0-60-42-102t-102-42q-60 0-102 42t-42 102q0 60 42 102t102 42Z" />
            </svg>

            <span>Setup</span>
          </div>

          <div>
            <Menu as="div" className="relative inline-block text-left">

              <MenuButton className="inline-flex w-56 justify-between items-center rounded-full bg-[#f8f7f4] px-3 py-2 text-sm font-medium text-gray-900 shadow hover:bg-[#f0eee9]">
                <span>
                  {user?.name || user?.email || 'User'}
                </span>

                <span className="material-symbols-outlined text-gray-400 text-[16px]">
                  arrow_drop_down
                </span>
              </MenuButton>

              <MenuItems className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">

                <div className="py-1">

                  {/* USER INFO */}
                  <div className="px-3 py-2 text-xs text-gray-400 border-b">
                    {user?.email}
                  </div>

                  {/* LOGOUT */}
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => {
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
  setUser(null);
}}

                        className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-3 py-2 text-sm text-red-600`}
                      >
                        Logout
                      </button>
                    )}
                  </MenuItem>

                </div>

              </MenuItems>

            </Menu>
          </div>

        </div>
      </div>

      {/* Main */}
      <div className="flex-1 p-6 overflow-auto">

        {view === 'pipeline' && (
          <div>

            {/* Header */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b">

              <div>
                <h1 className="text-2xl font-semibold">Complement Group</h1>

                <p className="text-sm text-gray-500">
                  Active Opportunities {filteredOpportunities.filter(op => !['won', 'lost'].includes(op.status)).length}
                </p>

                <p className="text-lg font-semibold">
                  {`$${filteredOpportunities
                    .reduce((sum, op) => sum + Number(op.value || 0), 0)
                    .toLocaleString('es-CO')}`}
                </p>
              </div>



            </div>

            {/* Dashboard */}
            <div className="grid grid-cols-5 gap-4 mb-6">

              {/* Pipeline Health */}
              <div className="bg-white p-4 rounded-[20px] shadow">

                {(() => {
                  const totalPipeline = filteredOpportunities
                    .reduce((sum, op) => sum + Number(op.value || 0), 0);

                  const total = filteredOpportunities.length;


                  // Trend (same logic you had)
                  const today = new Date();

                  const last30 = new Date();
                  last30.setDate(today.getDate() - 30);

                  const prev30 = new Date();
                  prev30.setDate(today.getDate() - 60);

                  const calc = (data) => {
                    const total = data.reduce((sum, op) => sum + Number(op.value || 0), 0);

                    const won = data
                      .filter(op => op.status === 'won')
                      .reduce((sum, op) => sum + Number(op.value || 0), 0);

                    return total ? (won / total) * 100 : 0;
                  };

                  const currentClosed = filteredOpportunities.filter(op =>
                    ['won', 'lost'].includes(op.status) &&
                    op.closed_at &&
                    new Date(op.closed_at) >= last30
                  );

                  const previousClosed = filteredOpportunities.filter(op =>
                    ['won', 'lost'].includes(op.status) &&
                    op.closed_at &&
                    new Date(op.closed_at) >= prev30 &&
                    new Date(op.closed_at) < last30
                  );

                  const trend = calc(currentClosed) - calc(previousClosed);

                  return (
                    <>
                      {/* Header */}
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-500">Total Pipeline</p>

                        <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' :
                          trend < 0 ? 'text-red-600' :
                            'text-gray-400'
                          }`}>
                          {trend > 0 ? '▲' : trend < 0 ? '▼' : '—'} {Math.abs(Math.round(trend))} pts
                        </span>
                      </div>

                      {/* Main KPI */}
                      <p className="text-2xl font-bold mt-2">
                        ${totalPipeline.toLocaleString('es-CO')}
                      </p>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mt-2">

                        <div>
                          <p className="text-gray-400">Opportunities</p>
                          <p className="font-semibold">{total}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Success Rate</p>
                          <p className="font-semibold">{' '}
                            {(() => {
                              const totalValue = filteredOpportunities.reduce((sum, op) => sum + Number(op.value || 0), 0);
                              const wonValue = filteredOpportunities
                                .filter(op => op.status === 'won')
                                .reduce((sum, op) => sum + Number(op.value || 0), 0);

                              return totalValue ? Math.round((wonValue / totalValue) * 100) : 0;
                            })()}%</p>

                        </div>

                      </div>




                    </>
                  );
                })()}

              </div>

              {/* Funnel */}
              <div className="lg:col-span-2 lg:row-span-2">
                <Funnel
                  opportunities={filteredOpportunities}
                  stageTiming={stageTiming}
                  mode={funnelMode}
                  setMode={setFunnelMode}
                />
              </div>

              {/* Active Pipeline */}
              <div className="bg-white p-4 rounded-[20px] shadow">

                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">Active Pipeline</p>
                  <span className="text-xs font-semibold text-gray-700">{filteredOpportunities.filter(op => !['won', 'lost'].includes(op.status)).length} Deals</span>
                </div>

                <p className="text-2xl font-bold mt-2">
                  {(() => {
                    const active = filteredOpportunities.filter(op => !['won', 'lost'].includes(op.status));

                    const totalValue = active
                      .reduce((sum, op) => sum + Number(op.value || 0), 0);

                    return `$${totalValue.toLocaleString('es-CO')}`;
                  })()}
                </p>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mt-2">

                  <div>
                    <p className="text-gray-400">At Risk </p>
                    <p className="font-semibold text-[#d97706]">{filteredOpportunities.filter(op =>
                      op.days_open > 5 && !['won', 'lost'].includes(op.status)
                    ).length} Deals</p>

                  </div>
                  <div>
                    <p className="text-gray-400">Weighted ({(() => {
                      const total = filteredOpportunities
                        .filter(op => !['won', 'lost'].includes(op.status))
                        .reduce((sum, op) => sum + Number(op.value || 0), 0);

                      const weighted = filteredOpportunities
                        .filter(op => !['won', 'lost'].includes(op.status))
                        .reduce((sum, op) => {
                          const value = Number(op.value || 0);
                          const prob = Number(op.probability || 0);
                          return sum + (value * prob / 100);
                        }, 0);

                      const pct = total ? (weighted / total) * 100 : 0;

                      return `${Math.round(pct)}%`;
                    })()})</p>
                    <p className="font-semibold text-gray-500">{`$${filteredOpportunities
                      .filter(op => !['won', 'lost'].includes(op.status))
                      .reduce((sum, op) => {
                        const value = Number(op.value || 0);
                        const prob = Number(op.probability || 0);
                        return sum + (value * prob / 100);
                      }, 0)
                      .toLocaleString('es-CO')}`}</p>
                  </div>
                </div>
              </div>

              {/* Avg Sales Cycle */}
              <div className="bg-white p-4 rounded-[20px] shadow">



                {(() => {

                  const closed = filteredOpportunities.filter(op =>
                    ['won', 'lost'].includes(op.status) &&
                    op.closed_at &&
                    op.created_at
                  );

                  const calcAvg = (data) => {
                    const totalDays = data.reduce((sum, op) => {
                      const created = new Date(op.created_at);
                      const closedDate = new Date(op.closed_at);
                      return sum + ((closedDate - created) / (1000 * 60 * 60 * 24));
                    }, 0);

                    return data.length ? totalDays / data.length : 0;
                  };

                  const wonDeals = closed.filter(op => op.status === 'won');
                  const lostDeals = closed.filter(op => op.status === 'lost');

                  const wonAvg = calcAvg(wonDeals);
                  const lostAvg = calcAvg(lostDeals);

                  const delta = wonAvg - lostAvg;

                  return (
                    <>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-500">Sales Cycle</p><span className={`text-xs font-semibold ${delta < 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {delta < 0 ? '▲' : '▼'} {Math.abs(Math.round(delta))}d
                        </span>
                      </div>

                      <p className="text-2xl font-bold mt-2">
                        {(() => {
                          const closed = filteredOpportunities.filter(op =>
                            ['won', 'lost'].includes(op.status) &&
                            op.closed_at &&
                            op.created_at
                          );

                          const totalDays = closed.reduce((sum, op) => {
                            const created = new Date(op.created_at);
                            const closedDate = new Date(op.closed_at);

                            const days = (closedDate - created) / (1000 * 60 * 60 * 24);

                            return sum + days;
                          }, 0);

                          const avg = closed.length ? totalDays / closed.length : 0;

                          return `${Math.round(avg)} days`;
                        })()}
                      </p>


                      {/* Values */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mt-2">

                        {/* Won */}
                        <div>
                          <p className="text-xs text-gray-400">Won</p>
                          <p className="text-xs font-bold text-[#016690]">
                            {Math.round(wonAvg)} days
                          </p>
                        </div>

                        {/* Lost */}
                        <div>
                          <p className="text-xs text-gray-400">Lost</p>
                          <p className="text-xs font-bold text-[#d97706]">
                            {Math.round(lostAvg)} days
                          </p>
                        </div>

                      </div>
                    </>
                  );
                })()}



              </div>

              {/* Conversion & Outcomes */}
              <div className="bg-white p-4 rounded-[20px] shadow">

                {(() => {
                  const closed = filteredOpportunities.filter(op => ['won', 'lost'].includes(op.status));

                  const totalClosed = closed.reduce((sum, op) => sum + Number(op.value || 0), 0);

                  const wonValue = closed
                    .filter(op => op.status === 'won')
                    .reduce((sum, op) => sum + Number(op.value || 0), 0);

                  const lostValue = closed
                    .filter(op => op.status === 'lost')
                    .reduce((sum, op) => sum + Number(op.value || 0), 0);

                  const wonPct = totalClosed ? (wonValue / totalClosed) * 100 : 0;
                  const lostPct = totalClosed ? (lostValue / totalClosed) * 100 : 0;

                  const winRate = closed.length
                    ? (closed.filter(op => op.status === 'won').length / closed.length) * 100
                    : 0;

                  const conversion = filteredOpportunities.length
                    ? (filteredOpportunities.filter(op => op.status === 'won').length / filteredOpportunities.length) * 100
                    : 0;

                  return (
                    <>
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">Closed Revenue</p>

                        {/* 👇 replaces old trend */}
                        <span className="text-xs font-semibold text-gray-700">
                          ${(totalClosed >= 1_000_000
                            ? (totalClosed / 1_000_000).toLocaleString('es-CO', { maximumFractionDigits: 1 }) + 'M'
                            : (totalClosed / 1_000).toLocaleString('es-CO', { maximumFractionDigits: 0 }) + 'K'
                          )}
                        </span>
                      </div>

                      {/* Bar */}
                      <div className="h-2 w-full bg-gray-200 rounded-full mt-2 mb-2 overflow-hidden flex">
                        <div className="bg-[#016690]" style={{ width: `${wonPct}%` }} />
                        <div className="bg-[#d97706]" style={{ width: `${lostPct}%` }} />
                      </div>

                      {/* Split */}
                      <div className="flex justify-between text-xs font-semibold">
                        <span className="text-[#016690]">{Math.round(wonPct)}% won</span>
                        <span className="text-[#d97706]">{Math.round(lostPct)}% lost</span>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mt-2">

                        <div>
                          <p className="text-gray-400">Conversion</p>
                          <p className="font-semibold">{Math.round(conversion)}%</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Win Rate</p>
                          <p className="font-semibold">{Math.round(winRate)}%</p>
                        </div>

                      </div>

                    </>
                  );
                })()}

              </div>

              {/* Projected Income */}
              <div className="bg-white p-4 rounded-[20px] shadow">
                <p className="text-sm text-gray-500">Projected Income</p>



                <p className="text-2xl font-bold mt-2">
                  {(() => {

                    // Active pipeline
                    const active = filteredOpportunities.filter(op => !['won', 'lost'].includes(op.status));
                    const activeValue = active.reduce((sum, op) => sum + Number(op.value || 0), 0);

                    // Closed deals
                    const closed = filteredOpportunities.filter(op => ['won', 'lost'].includes(op.status));

                    const totalClosed = closed.reduce((sum, op) => sum + Number(op.value || 0), 0);

                    const wonValue = closed
                      .filter(op => op.status === 'won')
                      .reduce((sum, op) => sum + Number(op.value || 0), 0);

                    const conversion = totalClosed ? (wonValue / totalClosed) : 0;

                    // Projection
                    const projected = activeValue * conversion;

                    return `$${Math.round(projected).toLocaleString('es-CO')}`;
                  })()}
                </p>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs mt-2">

                  <div>
                    <p className="text-gray-400">Based on won value</p>


                  </div>
                  <div>
                    <p className="text-gray-400">Avg Deal Size</p>
                    <p className="font-semibold text-gray-500">{(() => {
                      const wonDeals = filteredOpportunities.filter(op => op.status === 'won');

                      const totalWonValue = wonDeals
                        .reduce((sum, op) => sum + Number(op.value || 0), 0);

                      const countWonDeals = wonDeals.length;

                      const avg = countWonDeals ? totalWonValue / countWonDeals : 0;

                      return `$${Math.round(avg).toLocaleString('es-CO')}`;
                    })()}</p>
                  </div>
                </div>
              </div>

              {/* Stage Duration */}
              <div className="bg-white p-4 rounded-[20px] shadow">



              </div>

            </div>

            {/* Create */}
            <div className="bg-[#f0eee9] p-4 rounded-md shadow mb-6">
              <h3 className="font-semibold mb-3">Create Opportunity</h3>

              <div className="grid grid-cols-6 gap-3">
                <Menu as="div" className="relative inline-block text-left w-full">

                  <MenuButton className="inline-flex w-full justify-between items-center rounded-md bg-white px-3 py-2 text-sm ring-1 ring-gray-300 hover:bg-gray-50">
                    <span>
                      {form.account_id
                        ? accounts.find(a => a.id === Number(form.account_id))?.name
                        : 'Client'}
                    </span>
                    <span className="material-symbols-outlined text-gray-400 text-[16px]">
                      arrow_drop_down
                    </span>
                  </MenuButton>

                  <MenuItems className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">

                    <div className="py-1">

                      {[...accounts]
  .sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )
  .map(a => (
                        <MenuItem key={a.id}>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                setForm({ ...form, account_id: a.id })
                              }
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-3 py-2 text-sm ${form.account_id === a.id ? 'font-medium' : ''
                                }`}
                            >
                              {a.name}
                            </button>
                          )}
                        </MenuItem>
                      ))}

                    </div>

                  </MenuItems>

                </Menu>

                <Menu as="div" className="relative inline-block text-left w-full">

                  <MenuButton className="inline-flex w-full justify-between items-center rounded-md bg-white px-3 py-2 text-sm ring-1 ring-gray-300 hover:bg-gray-50">
                    <span>
                      {form.owner_ref
                        ? owners.find(o => o.id === Number(form.owner_ref))?.name
                        : 'Owner'}
                    </span>
                    <span className="material-symbols-outlined text-gray-400 text-[16px]">
                      arrow_drop_down
                    </span>
                  </MenuButton>

                  <MenuItems className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">

                    <div className="py-1">

                      {[...owners]
  .sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )
  .map(o => (
                        <MenuItem key={o.id}>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                setForm({ ...form, owner_ref: o.id })
                              }
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-3 py-2 text-sm ${form.owner_ref === o.id ? 'font-medium' : ''
                                }`}
                            >
                              {o.name}
                            </button>
                          )}
                        </MenuItem>
                      ))}

                    </div>

                  </MenuItems>

                </Menu>

                <Menu as="div" className="relative inline-block text-left w-full">

                  <MenuButton className="inline-flex w-full justify-between items-center rounded-md bg-white px-3 py-2 text-sm ring-1 ring-gray-300 hover:bg-gray-50">
                    <span>
                      {form.lob_id
                        ? lobs.find(l => l.id === Number(form.lob_id))?.name
                        : 'LOB'}
                    </span>
                    <span className="material-symbols-outlined text-gray-400 text-[16px]">
                      arrow_drop_down
                    </span>
                  </MenuButton>

                  <MenuItems className="absolute z-10 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">

                    <div className="py-1">

                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() =>
                              setForm({ ...form, lob_id: '' })
                            }
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-3 py-2 text-sm`}
                          >
                            No LOB
                          </button>
                        )}
                      </MenuItem>

                      {lobs.map(l => (
                        <MenuItem key={l.id}>
                          {({ active }) => (
                            <button
                              onClick={() =>
                                setForm({ ...form, lob_id: l.id })
                              }
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-3 py-2 text-sm ${form.lob_id === l.id ? 'font-medium' : ''
                                }`}
                            >
                              {l.name}
                            </button>
                          )}
                        </MenuItem>
                      ))}

                    </div>

                  </MenuItems>

                </Menu>

                <input
                  name="name"
                  placeholder="Project"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full inline-flex items-center rounded-md bg-white px-3 py-2 text-sm ring-1 ring-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                />

                <input
                  name="value"
                  type="text"
                  placeholder="Value ($)"
                  value={
                    form.value
                      ? `$${Number(form.value).toLocaleString('es-CO')}`
                      : ''
                  }
                  onChange={(e) => {
                    let raw = e.target.value.replace(/\D/g, ''); // keep only digits

                    setForm({
                      ...form,
                      value: raw
                    });
                  }}
                  className="w-full inline-flex items-center rounded-md bg-white px-3 py-2 text-sm ring-1 ring-gray-300 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                />

                <button
                  onClick={createOpportunity}
                  className="
  px-4 py-2 rounded-md text-sm font-medium text-white
  bg-[#016690]
  shadow-[0_2px_4px_rgba(0,0,0,0.15),_0_4px_8px_rgba(1,102,144,0.25)]
  hover:bg-[#075173]
  active:bg-[#05344c]
  active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.2)]
  transition-all duration-150
"
                >
                  Create
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex justify-between items-center mb-6 bg-[#f8f7f4] py-2">

              <div className="relative group">

                <button
                  onClick={() =>
                    setViewMode(prev => (prev === 'kanban' ? 'list' : 'kanban'))
                  }
                  className="w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">

                    {viewMode === 'kanban' ? (

                      // LIST ICON
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 -960 960 960"
                        className="w-4 h-4 block"
                        fill="#016690"
                      >
                        <path d="M336-192h456q29.7 0 50.85-21.15Q864-234.3 864-264v-72H336v144ZM96-624h168v-144h-96q-29.7 0-50.85 21.15Q96-725.7 96-696v72Zm0 216h168v-144H96v144Zm72 216h96v-144H96v72.21Q96-234 117.15-213T168-192Zm168-216h528v-144H336v144Zm0-216h528v-72.21Q864-726 842.85-747T792-768H336v144Z" />
                      </svg>

                    ) : (

                      // KANBAN ICON
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 -960 960 960"
                        className="w-4 h-4 block"
                        fill="#016690"
                      >
                        <path d="M216-144q-29.7 0-50.85-21.15Q144-186.3 144-216v-528q0-29.7 21.15-50.85Q186.3-816 216-816h528q29.7 0 50.85 21.15Q816-773.7 816-744v528q0 29.7-21.15 50.85Q773.7-144 744-144H216Zm72-144h72v-384h-72v384Zm312-96h72v-288h-72v288Zm-156-96h72v-192h-72v192Z" />
                      </svg>

                    )}

                  </div>
                </button>

                {/* Tooltip */}
                <div className="
  absolute left-full ml-2 top-1/2 -translate-y-1/2
  px-2 py-1 text-[10px] text-white bg-[#075173] rounded
  opacity-0 group-hover:opacity-100
  transition pointer-events-none whitespace-nowrap
">
                  {viewMode === 'kanban' ? 'List view' : 'Kanban view'}
                </div>

              </div>

              <div className="flex items-center gap-3">



                {(
                  [clientFilter, lobFilter, ownerFilter, actionFilter]
                    .filter(f => f !== 'all').length > 1
                ) && (

                    <div className="relative group ml-2">

                      <button
                        onClick={() => {
                          setClientFilter('all');
                          setLobFilter('all');
                          setOwnerFilter('all');
                          setActionFilter('all');
                        }}
                        className="flex items-center justify-center cursor-pointer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 -960 960 960"
                          className="w-4 h-4 hover:fill-[#075173] transition"
                          fill="#016690"
                        >
                          <path d="m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141 51 51ZM480-96q-79 0-149-30t-122.5-82.5Q156-261 126-331T96-480q0-80 30-149.5t82.5-122Q261-804 331-834t149-30q80 0 149.5 30t122 82.5Q804-699 834-629.5T864-480q0 79-30 149t-82.5 122.5Q699-156 629.5-126T480-96Z" />
                        </svg>
                      </button>

                      {/* Tooltip */}
                      <div className="
  absolute right-full mr-2 top-1/2 -translate-y-1/2
  px-1 py-1 text-[10px] text-[#016690] rounded
  opacity-0 group-hover:opacity-100
  transition pointer-events-none whitespace-nowrap transition delay-75
">
                        Clear all
                      </div>

                    </div>

                  )}

                {/* CLIENT FILTER */}
                <Menu as="div" className="relative inline-block text-left">
                  <div className={`group inline-flex w-40 items-center rounded-full  ${clientFilter === 'all'
                    ? 'bg-[#f8f7f4] text-[#907a66] shadow'
                    : 'bg-[#dcd7cc] text-[#907a66] shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)] hover:bg-[#f0eee9] hover:text-[#907a66]'
                    }`}>




                    <MenuButton className="flex-1 flex justify-between items-center px-2 py-1 text-xs hover:bg-[#f0eee9] rounded-full">
                      <span className="truncate max-w-[100px]">
                        {clientFilter === 'all' ? 'Client' : clientFilter}
                      </span>

                      {clientFilter === 'all' && (
                        <svg className="w-4 h-4 text-[#907a66] shrink-0" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="M384-264v-72h192v72H384ZM240-444v-72h480v72H240Zm-96-180v-72h672v72H144Z" />
                        </svg>
                      )}
                    </MenuButton>

                    {clientFilter !== 'all' && (
                      <div
                        onClick={() => setClientFilter('all')}
                        className="px-1 cursor-pointer flex items-center"
                      >
                        <svg className="w-5 h-5 text-current group-hover:text-[#907166]" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141 51 51Z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <MenuItems className="absolute right-0 z-50 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setClientFilter('all')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            All
                          </button>
                        )}
                      </MenuItem>

                      {[...new Set(opportunities.map(op => op.client))].map(c => (
                        <MenuItem key={c}>
                          {({ active }) => (
                            <button
                              onClick={() => setClientFilter(c)}
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs ${clientFilter === c ? 'font-medium' : ''}`}
                            >
                              {c}
                            </button>
                          )}
                        </MenuItem>
                      ))}
                    </div>
                  </MenuItems>
                </Menu>

                {/* LOB FILTER */}
                <Menu as="div" className="relative inline-block text-left">
                  <div className={`group inline-flex w-40 items-center rounded-full ${lobFilter === 'all'
                    ? 'bg-[#f8f7f4] text-[#907a66] shadow'
                    : 'bg-[#dcd7cc] text-[#907a66] shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)] hover:bg-[#f0eee9] hover:text-[#907a66]'
                    }`}>

                    <MenuButton className="flex-1 flex justify-between items-center px-2 py-1 text-xs hover:bg-[#f0eee9] rounded-full">
                      <span className="truncate max-w-[100px]">
                        {lobFilter === 'all' ? 'LOB' : lobFilter}
                      </span>

                      {lobFilter === 'all' && (
                        <svg className="w-4 h-4 text-[#907a66] shrink-0" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="M384-264v-72h192v72H384ZM240-444v-72h480v72H240Zm-96-180v-72h672v72H144Z" />
                        </svg>
                      )}
                    </MenuButton>

                    {lobFilter !== 'all' && (
                      <div
                        onClick={() => setLobFilter('all')}
                        className="px-1 cursor-pointer flex items-center"
                      >
                        <svg className="w-5 h-5 text-current group-hover:text-[#907166]" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141 51 51Z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <MenuItems className="absolute right-0 z-50 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setLobFilter('all')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            All
                          </button>
                        )}
                      </MenuItem>

                      {lobs.map(l => (
                        <MenuItem key={l.id}>
                          {({ active }) => (
                            <button
                              onClick={() => setLobFilter(l.name)}
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs ${lobFilter === l.name ? 'font-medium' : ''}`}
                            >
                              {l.name}
                            </button>
                          )}
                        </MenuItem>
                      ))}
                    </div>
                  </MenuItems>
                </Menu>

                {/* OWNER FILTER */}
                <Menu as="div" className="relative inline-block text-left">
                  <div className={`group inline-flex w-40 items-center rounded-full ${ownerFilter === 'all'
                    ? 'bg-[#f8f7f4] text-[#907a66] shadow'
                    : 'bg-[#dcd7cc] text-[#907a66] shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)] hover:bg-[#f0eee9] hover:text-[#907a66]'
                    }`}>

                    <MenuButton className="flex-1 flex justify-between items-center px-2 py-1 text-xs hover:bg-[#f0eee9] rounded-full">
                      <span className="truncate max-w-[100px]">
                        {ownerFilter === 'all' ? 'Owner' : ownerFilter}
                      </span>

                      {ownerFilter === 'all' && (
                        <svg className="w-4 h-4 text-[#907a66] shrink-0" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="M384-264v-72h192v72H384ZM240-444v-72h480v72H240Zm-96-180v-72h672v72H144Z" />
                        </svg>
                      )}
                    </MenuButton>

                    {ownerFilter !== 'all' && (
                      <div
                        onClick={() => setOwnerFilter('all')}
                        className="px-1 cursor-pointer flex items-center"
                      >
                        <svg className="w-5 h-5 text-current group-hover:text-[#907166]" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141 51 51Z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <MenuItems className="absolute right-0 z-50 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black/5 max-h-60 overflow-y-auto">
                    <div className="py-1">
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setOwnerFilter('all')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            All
                          </button>
                        )}
                      </MenuItem>

                      {owners.map(o => (
                        <MenuItem key={o.id}>
                          {({ active }) => (
                            <button
                              onClick={() => setOwnerFilter(o.name)}
                              className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs ${ownerFilter === o.name ? 'font-medium' : ''}`}
                            >
                              {o.name}
                            </button>
                          )}
                        </MenuItem>
                      ))}
                    </div>
                  </MenuItems>
                </Menu>

                {/* ACTION FILTER */}
                <Menu as="div" className="relative inline-block text-left">
                  <div className={`group inline-flex w-40 items-center rounded-full ${actionFilter === 'all'
                    ? 'bg-[#f8f7f4] text-[#907a66] shadow'
                    : 'bg-[#dcd7cc] text-[#907a66] shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)] hover:bg-[#f0eee9] hover:text-[#907a66]'
                    }`}>

                    <MenuButton className="flex-1 flex justify-between items-center px-2 py-1 text-xs hover:bg-[#f0eee9] rounded-full">
                      <span className="truncate max-w-[100px]">
                        {
                          actionFilter === 'all'
                            ? 'Action'
                            : actionFilter === 'due'
                              ? 'Action Due'
                              : actionFilter === 'overdue'
                                ? 'Overdue'
                                : 'Inactive'
                        }
                      </span>

                      {actionFilter === 'all' && (
                        <svg className="w-4 h-4 text-[#907a66] shrink-0" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="M384-264v-72h192v72H384ZM240-444v-72h480v72H240Zm-96-180v-72h672v72H144Z" />
                        </svg>
                      )}
                    </MenuButton>

                    {actionFilter !== 'all' && (
                      <div
                        onClick={() => setActionFilter('all')}
                        className="px-1 cursor-pointer flex items-center"
                      >
                        <svg className="w-5 h-5 text-current group-hover:text-[#907166]" viewBox="0 -960 960 960" fill="currentColor">
                          <path d="m339-288 141-141 141 141 51-51-141-141 141-141-51-51-141 141-141-141-51 51 141 141-141 141 51 51Z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <MenuItems className="absolute right-0 z-50 mt-1 w-40 rounded-md bg-white shadow-lg ring-1 ring-black/5">
                    <div className="py-1">

                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setActionFilter('all')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            All
                          </button>
                        )}
                      </MenuItem>

                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setActionFilter('due')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            Action Due
                          </button>
                        )}
                      </MenuItem>

                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setActionFilter('overdue')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            Overdue
                          </button>
                        )}
                      </MenuItem>

                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => setActionFilter('inactive')}
                            className={`${active ? 'bg-gray-100' : ''} block w-full text-left px-2 py-1 text-xs`}
                          >
                            Inactive
                          </button>
                        )}
                      </MenuItem>

                    </div>
                  </MenuItems>
                </Menu>

              </div>
            </div>

            {viewMode === 'kanban' && (
              <div className="flex gap-4">
                {['lead', 'qualified', 'proposal', 'won', 'lost'].map(stage => (
                  <div
                    key={stage}
                    onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage); }}
                    onDragLeave={() => setDragOverStage(null)}
                    onDrop={(e) => {
                      setDraggingId(null);
                      setDragOverStage(null);

                      const id = Number(e.dataTransfer.getData('opportunityId'));

                      const previous = opportunities;

                      // ✅ Optimistic update
                      setOpportunities(prev =>
                        prev.map(op =>
                          op.id === id ? { ...op, status: stage } : op
                        )
                      );

                      console.log('PATCH CALL →', {
                        id,
                        stage,
                        user_id: user.id
                      });

                      fetch(`${API}/opportunities/${id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          status: stage,
                          user_id: user.id
                        })
                      })
                        .then(async (res) => {
                          if (!res.ok) {
                            setOpportunities(previous); // rollback
                            return;
                          }

                          const updated = await res.json();

                          // ✅ sync exact backend response (avoids drift)
                          setOpportunities(prev =>
                            prev.map(op =>
                              op.id === id ? { ...op, ...updated } : op
                            )
                          );
                        })
                        .catch(() => {
                          setOpportunities(previous);
                        });
                    }}
                    className={`flex-1 p-1 rounded-md min-h-[300px] relative overflow-visible ${dragOverStage === stage ? 'bg-[#f8f7f4] border-2 border-[#c5bcac]' : 'bg-[#f8f7f4]'
                      //
                      }`}
                  >
                    <h3 className="font-semibold text-xs capitalize mb-3 text-[#907a66]">{stage}</h3>

                    {filteredOpportunities
                      .filter(op => op.status === stage)
                      .map(op => {

                        const isOverdue =
                          !['won', 'lost'].includes(op.status) &&
                          op.est_close_date &&
                          new Date(op.est_close_date) < new Date();

                        const isActionDue =
                          !['won', 'lost'].includes(op.status) &&
                          op.next_action_date &&
                          new Date(op.next_action_date) <= new Date();

                        const referenceDate = op.last_activity || op.created_at;

                        const isInactive =
                          !['won', 'lost'].includes(op.status) &&
                          (new Date() - new Date(referenceDate)) / (1000 * 60 * 60 * 24) > 7;

                        return (
                          <div
                            key={op.id}
                            draggable={false}
                            onDragEnd={() => {
                              setDraggingId(null);
                              setDragOverStage(null);
                            }}
                            onClick={() =>
                              setOpenCards(prev => ({
                                ...prev,
                                [op.id]: !prev[op.id]
                              }))
                            }

                            onMouseEnter={() => {
                              const t = setTimeout(() => setHoveredCard(op.id), 200);
                              setHoverTimeout(t);
                            }}
                            onMouseLeave={() => {
                              if (hoverTimeout) clearTimeout(hoverTimeout);
                              setHoveredCard(null);
                            }}
                            //STOP
                            className={`group bg-white ring-1 ring-[#f0eee9] p-3 mb-3 rounded-[20px] shadow cursor-pointer relative transition-all duration-200 overflow-hidden ${draggingId === op.id
                              ? 'opacity-20 scale-95'
                              : isOverdue
                                ? 'bg-[#fff6ed] ring-1 ring-[#ffd1a8]'
                                : isActionDue
                                  ? 'bg-white ring-1 ring-[#ffead4]'
                                  : isInactive
                                    ? 'bg-amber-50 ring-1 ring-amber-100'
                                    : 'bg-white'
                              }`}
                          >

                            {/* Header */}
                            <div className="mb-2">

                              {/* Top row: pill + handle (if pill exists) */}
                              {(isOverdue || isActionDue || isInactive) && (
                                <div className="flex justify-between items-center mb-1">

                                  {/* Pill */}
                                  {isOverdue ? (
                                    <div className="bg-[#fe6411] text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                      Overdue
                                    </div>
                                  ) : isActionDue ? (
                                    <div className="bg-[#ffead4] text-[#fe6411] text-[10px] px-1.5 py-0.5 rounded-full">
                                      Action Due
                                    </div>
                                  ) : isInactive ? (
                                    <div className="bg-amber-500 text-amber-100 text-[10px] px-1.5 py-0.5 rounded-full">
                                      {Math.floor((new Date() - new Date(op.last_activity || op.created_at)) / (1000 * 60 * 60 * 24))} Days
                                    </div>
                                  ) : <div />}

                                  {/* Right side: delete + handle */}
                                  <div className="flex items-center gap-1">

                                    {/* DELETE (admin only) */}
                                    {canDelete() && openCards[op.id] && (
                                      <span
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          if (!window.confirm('Delete this opportunity?')) return;

                                          fetch(`${API}/opportunities/${op.id}`, {
                                            method: 'DELETE',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ user_id: user.id })
                                          })
                                            .then(async res => {
                                              console.log('DELETE status:', res.status);

                                              const text = await res.text();
                                              console.log('DELETE response:', text);

                                              if (!res.ok) throw new Error('Delete failed');

                                              setOpportunities(prev => prev.filter(o => o.id !== op.id));
                                            })
                                            .catch(err => {
                                              console.error('DELETE error:', err);
                                            });
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#2B231F">
                                          <path d="M312-144q-29.7 0-50.85-21.15Q240-186.3 240-216v-480h-48v-72h192v-48h192v48h192v72h-48v479.57Q720-186 698.85-165T648-144H312Zm336-552H312v480h336v-480ZM384-288h72v-336h-72v336Zm120 0h72v-336h-72v336Z" />
                                        </svg>
                                      </span>
                                    )}

                                    {/* DRAG HANDLE */}
                                    <span
                                      draggable={!isSliding && (user?.role === 'admin' || !['won', 'lost'].includes(op.status))}
                                      onDragStart={(e) => {
                                        e.stopPropagation();

                                        if (user?.role !== 'admin' && ['won', 'lost'].includes(op.status)) return;

                                        e.dataTransfer.setData('opportunityId', op.id);
                                        e.dataTransfer.effectAllowed = 'move';

                                        setDraggingId(op.id);
                                      }}
                                      onDragEnd={() => {
                                        setDraggingId(null);
                                        setDragOverStage(null);
                                      }}
                                      className={`material-symbols-outlined text-[#dcd7cc] text-[14px] leading-none
    ${(user?.role !== 'admin' && ['won', 'lost'].includes(op.status))
                                          ? 'cursor-not-allowed opacity-40'
                                          : 'cursor-grab active:cursor-grabbing hover:text-gray-500'
                                        }`}
                                    >
                                      drag_indicator
                                    </span>

                                  </div>

                                </div>
                              )}

                              {/* Client + LOB + Owner + handle (if NO pill) */}
                              <div className="flex justify-between items-start gap-2">

                                {/* LEFT STACK */}
                                <div className="flex flex-col">

                                  {/* Client */}
                                  <span className="text-[13px] font-bold break-words text-[#2b231f] ">
                                    {op.client}
                                  </span>

                                  {/* LOB */}
                                  {editingLobId === op.id ? (

                                    <select
                                      autoFocus
                                      value={op.lob_id ?? ''}
                                      onChange={(e) => {
                                        const newLob = e.target.value ? Number(e.target.value) : null;

                                        setOpportunities(prev =>
                                          prev.map(o =>
                                            o.id === op.id
                                              ? {
                                                ...o,
                                                lob_id: newLob,
                                                line_of_business: lobs.find(x => x.id === newLob)?.name
                                              }
                                              : o
                                          )
                                        );

                                        fetch(`${API}/opportunities/${op.id}`, {
                                          method: 'PATCH',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            lob_id: newLob,
                                            owner_ref: user.id
                                          })
                                        });

                                        setEditingLobId(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingLobId(null);
                                        }
                                      }}
                                      className="border p-1 rounded text-xs w-full mb-1"
                                    >
                                      <option value="">No LOB</option>
                                      {lobs.map(l => (
                                        <option key={l.id} value={l.id}>{l.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span
                                      className="text-[10px] text-[#9d8a72] cursor-pointer mt-0.5 hover:text-orange-500"
                                      onClick={() => canEditLob() && setEditingLobId(op.id)}
                                    >
                                      {op.line_of_business || 'Select LOB'}
                                    </span>
                                  )}

                                  {/* OWNER */}
                                  {editingOwnerId === op.id ? (
                                    <select
                                      autoFocus
                                      value={op.owner_id}
                                      onChange={(e) => {
                                        const newOwner = Number(e.target.value);

                                        setOpportunities(prev =>
                                          prev.map(o =>
                                            o.id === op.id
                                              ? {
                                                ...o,
                                                owner_id: newOwner,
                                                owner: owners.find(x => x.id === newOwner)?.name
                                              }
                                              : o
                                          )
                                        );

                                        fetch(`${API}/opportunities/${op.id}`, {
                                          method: 'PATCH',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            owner_ref: newOwner,   // deal owner
                                            user_id: user.id       // acting user
                                          })
                                        });

                                        setEditingOwnerId(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          e.preventDefault();
                                          setEditingOwnerId(null);
                                        }
                                      }}
                                      className="border p-1 rounded text-xs w-full mb-1"
                                    >
                                      {owners.map(o => (
                                        <option key={o.id} value={o.id}>{o.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span
                                      className="text-[11px] text-[#c5bcac] mt-0.5 cursor-pointer hover:text-orange-500"
                                      onClick={() => canEditOwner() && setEditingOwnerId(op.id)}
                                    >
                                      {op.owner}
                                    </span>
                                  )}

                                </div>

                                {/* Handle ONLY if no pill */}
                                {!isOverdue && !isActionDue && !isInactive && (
                                  <div className="flex items-center gap-1">

                                    {/* DELETE (admin only) */}
                                    {canDelete() && openCards[op.id] && (
                                      <span
                                        onClick={(e) => {
                                          e.stopPropagation();

                                          if (!window.confirm('Delete this opportunity?')) return;

                                          fetch(`${API}/opportunities/${op.id}`, {
                                            method: 'DELETE',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ user_id: user.id })
                                          })
                                            .then(async res => {
                                              console.log('DELETE status:', res.status);

                                              const text = await res.text();
                                              console.log('DELETE response:', text);

                                              if (!res.ok) throw new Error('Delete failed');

                                              setOpportunities(prev => prev.filter(o => o.id !== op.id));
                                            })
                                            .catch(err => {
                                              console.error('DELETE error:', err);
                                            });
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#2B231F">
                                          <path d="M312-144q-29.7 0-50.85-21.15Q240-186.3 240-216v-480h-48v-72h192v-48h192v48h192v72h-48v479.57Q720-186 698.85-165T648-144H312Zm336-552H312v480h336v-480ZM384-288h72v-336h-72v336Zm120 0h72v-336h-72v336Z" />
                                        </svg>
                                      </span>
                                    )}

                                    {/* DRAG HANDLE */}
                                    <span
                                      draggable={!isSliding && canDrag(op)}
                                      onDragStart={(e) => {
                                        e.stopPropagation();

                                        if (!canDrag(op)) return;

                                        e.dataTransfer.setData('opportunityId', op.id);
                                        e.dataTransfer.effectAllowed = 'move';

                                        setDraggingId(op.id);
                                      }}
                                      onDragEnd={() => {
                                        setDraggingId(null);
                                        setDragOverStage(null);
                                      }}
                                      className={`material-symbols-outlined text-[#dcd7cc] text-[14px] leading-none
    ${!canDrag(op)
                                          ? 'cursor-not-allowed opacity-40'
                                          : 'cursor-grab active:cursor-grabbing hover:text-gray-500'
                                        }`}
                                    >
                                      drag_indicator
                                    </span>

                                  </div>
                                )}

                              </div>

                            </div>

                            {/* Project */}
                            <div
                              className="text-base text-[#907a66] font-semibold leading-snug overflow-hidden pr-2 mb-3"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                              title={op.project}
                            >
                              {op.project}
                            </div>

                            {/* Value row */}
                            <div className="flex justify-end mt-1 mb-1">

                              {editingValueId === op.id ? (
                                <input
                                  autoFocus
                                  type="text"
                                  value={form[`value_${op.id}`] || ''}
                                  onChange={(e) => {
                                    let raw = e.target.value.replace(/\D/g, '');
                                    setForm({
                                      ...form,
                                      [`value_${op.id}`]: raw
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.target.blur(); // triggers save
                                    }

                                    if (e.key === 'Escape') {
                                      e.preventDefault();

                                      setForm({
                                        ...form,
                                        [`value_${op.id}`]: op.value || ''
                                      });

                                      setEditingValueId(null);
                                    }
                                  }}
                                  onBlur={() => {
                                    const newValue = Number(form[`value_${op.id}`] || 0);

                                    setOpportunities(prev =>
                                      prev.map(o =>
                                        o.id === op.id ? { ...o, value: newValue } : o
                                      )
                                    );

                                    fetch(`${API}/opportunities/${op.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        value: newValue,
                                        user_id: user.id
                                      })
                                    }).then(() => {

                                      return fetch(`${API}/activities`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          opportunity_id: op.id,
                                          owner_id: user.id,
                                          note: `Value updated to $${newValue.toLocaleString('es-CO')}`,
                                          activity_type: 'Update'
                                        })
                                      });

                                    }).then(() => {
                                      fetchOpportunities();
                                    });

                                    setEditingValueId(null);
                                  }}
                                  className="w-24 text-right px-2 py-0.5 text-[10px] border rounded outline-none"
                                />
                              ) : (
                                <span
                                  onClick={() => {
                                    if (!canEditValue()) return;
                                    setEditingValueId(op.id);
                                    setForm({
                                      ...form,
                                      [`value_${op.id}`]: op.value || ''
                                    });
                                  }}
                                  className="px-2 py-0.5 text-[10px] bg-[#0076a9] text-[#f0eee9] rounded-full font-medium cursor-pointer hover:bg-[#016690]"
                                >
                                  ${Number(op.value || 0).toLocaleString('es-CO')}
                                </span>
                              )}

                            </div>

                            {/* Next Action */}
                            {op.next_action_date && (
                              <div
                                className={`flex justify-end text-[10px] mt-4 ${new Date(op.next_action_date) <= new Date()
                                  ? 'text-[#fe6411] font-medium'
                                  : 'text-[#0095d0]'
                                  }`}
                              >
                                Next Activity: {new Date(op.next_action_date).toLocaleDateString('es-CO')}
                              </div>
                            )}

                            {/* EXPAND */}
                            <div
                              className={`transition-all duration-300 ease-in-out overflow-hidden ${openCards[op.id] || hoveredCard === op.id
                                ? 'max-h-[500px] opacity-100 mt-2'
                                : 'max-h-0 opacity-0'
                                }`}
                              onClick={(e) => e.stopPropagation()}
                            >





                              {/* Dates */}
                              <div className="text-xs text-gray-500 mt-1">
                                Open: <strong>{op.days_open} days</strong>
                              </div>

                              {/* Due Date */}
                              <div
                                className={`text-xs ${op.est_close_date && new Date(op.est_close_date) < new Date()
                                  ? 'text-orange-600 font-medium'
                                  : 'text-gray-500'
                                  } mb-4`}
                              >
                                Due Date: <strong>{op.est_close_date
                                  ? new Date(op.est_close_date).toLocaleDateString('es-CO')
                                  : '—'}</strong>
                              </div>

                              <div className="text-xs mt-1">
                                Probability:{' '}

                                {editingProbabilityId === op.id ? (
                                  <input
                                    autoFocus
                                    type="text"
                                    value={form[`prob_${op.id}`] || ''}
                                    onChange={(e) => {
                                      let val = e.target.value.replace(/\D/g, '');
                                      if (val > 100) val = 100;

                                      setForm({
                                        ...form,
                                        [`prob_${op.id}`]: val
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.target.blur();
                                      }

                                      if (e.key === 'Escape') {
                                        e.preventDefault();

                                        setForm({
                                          ...form,
                                          [`prob_${op.id}`]: op.probability || ''
                                        });

                                        setEditingProbabilityId(null);
                                      }
                                    }}
                                    onBlur={() => {
                                      const newProb = Number(form[`prob_${op.id}`] || 0);

                                      setOpportunities(prev =>
                                        prev.map(o =>
                                          o.id === op.id
                                            ? { ...o, probability: newProb }
                                            : o
                                        )
                                      );

                                      fetch(`${API}/opportunities/${op.id}`, {
                                        method: 'PATCH',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          probability: newProb,
                                          user_id: user.id
                                        })
                                      }).then(() => {

                                        return fetch(`${API}/activities`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            opportunity_id: op.id,
                                            owner_id: user.id,
                                            note: `Probability updated to ${newProb}%`,
                                            activity_type: 'Update'
                                          })
                                        });

                                      }).then(() => {
                                        fetchOpportunities();
                                      });

                                      setEditingProbabilityId(null);
                                    }}
                                    className="w-8 text-xs border rounded px-1 ml-1"
                                  />
                                ) : (
                                  <span
                                    onClick={() => {
                                      if (!canEditProbability()) return;
                                      setEditingProbabilityId(op.id);
                                      setForm({
                                        ...form,
                                        [`prob_${op.id}`]: op.probability || ''
                                      });
                                    }}
                                    className="cursor-pointer"
                                  >
                                    {op.probability}%
                                  </span>
                                )}
                              </div>



                              {/* Progress */}
                              <div
                                className="h-2 bg-[#dcd7cc] mt-2 rounded mb-4 relative cursor-pointer w-[95%]"
                                onDragStart={(e) => e.stopPropagation()}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  setIsSliding(true);
                                  if (!canEditProbability()) return;

                                  const rect = e.currentTarget.getBoundingClientRect();

                                  const update = (clientX) => {
                                    let raw = ((clientX - rect.left) / rect.width) * 100;
                                    let newProb = Math.round(raw / 10) * 10;
                                    if (newProb < 0) newProb = 0;
                                    if (newProb > 100) newProb = 100;

                                    setOpportunities(prev =>
                                      prev.map(o =>
                                        o.id === op.id ? { ...o, probability: newProb } : o
                                      )
                                    );

                                    return newProb;
                                  };

                                  let latestProb = update(e.clientX);

                                  const handleMove = (ev) => {
                                    latestProb = update(ev.clientX);
                                  };

                                  const handleUp = () => {
                                    setIsSliding(false);
                                    document.removeEventListener('mousemove', handleMove);
                                    document.removeEventListener('mouseup', handleUp);

                                    fetch(`${API}/opportunities/${op.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        probability: latestProb,
                                        user_id: user.id
                                      })
                                    }).then(() => {

                                      return fetch(`${API}/activities`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          opportunity_id: op.id,
                                          owner_id: user.id,
                                          note: `Probability updated to ${latestProb}%`,
                                          activity_type: 'Update'
                                        })
                                      });

                                    }).then(() => {
                                      fetchOpportunities();
                                    });
                                  };

                                  document.addEventListener('mousemove', handleMove);
                                  document.addEventListener('mouseup', handleUp);
                                }}
                              >

                                {/* Filled bar */}
                                <div
                                  className="h-2 bg-[#7bc861] rounded"
                                  style={{ width: `${op.probability}%` }}
                                />

                                {/* Handle */}
                                <div
                                  className="absolute top-1/2 -translate-y-1/2 w-2 h-3 bg-white border-2 border-[#7bc861] rounded-full"
                                  style={{ left: `calc(${op.probability}% - 8px)` }}
                                />

                              </div>

                              {/* Last note */}
                              <div className="text-xs text-gray-500 mt-1">
                                {op.last_activity_note || 'No notes yet'}
                              </div>



                              <div className="mt-2 space-y-1">

                                {/* Row 1: Type + Date */}
                                <div className="flex gap-2">
                                  <select
                                    value={form[`type_${op.id}`] || ''}
                                    onChange={(e) =>
                                      setForm({
                                        ...form,
                                        [`type_${op.id}`]: e.target.value
                                      })
                                    }
                                    className="border p-1 rounded text-xs w-1/2"
                                  >
                                    <option value="">Type</option>
                                    <option value="Proposal Sent">Proposal Sent</option>
                                    <option value="Client Meeting">Client Meeting</option>
                                    <option value="Follow-up">Follow-up</option>
                                    <option value="Negotiation">Negotiation</option>
                                    <option value="LOI Received">LOI Received</option>
                                    <option value="Contract Sent">Contract Sent</option>
                                  </select>

                                  <input
                                    type="date"
                                    value={form[`next_${op.id}`] || ''}
                                    onChange={(e) =>
                                      setForm({
                                        ...form,
                                        [`next_${op.id}`]: e.target.value
                                      })
                                    }
                                    className="border p-1 rounded text-xs w-1/2"
                                  />
                                </div>

                                {/* Row 2: Note + Button */}
                                <div className="flex gap-2">
                                  <input
                                    placeholder="Note..."
                                    value={form[`note_${op.id}`] || ''}
                                    onChange={(e) =>
                                      setForm({
                                        ...form,
                                        [`note_${op.id}`]: e.target.value
                                      })
                                    }
                                    className="border p-1 rounded flex-1 text-xs"
                                  />

                                  <button
                                    onClick={() => {
                                      if (!user?.id) {
                                        alert('User not authenticated');
                                        return;
                                      }

                                      const note = form[`note_${op.id}`];
                                      const type = form[`type_${op.id}`];
                                      const next = form[`next_${op.id}`];

                                      if (!type && !note) return;

                                      fetch(`${API}/activities`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          opportunity_id: op.id,
                                          owner_id: user.id,
                                          note,
                                          activity_type: type,
                                          next_action_date: next || null
                                        })
                                      }).then(() => {
                                        fetchOpportunities();

                                        setForm({
                                          ...form,
                                          [`note_${op.id}`]: '',
                                          [`type_${op.id}`]: '',
                                          [`next_${op.id}`]: ''
                                        });
                                      });
                                    }}
                                    className="bg-gray-800 text-white px-2 rounded text-xs"
                                  >
                                    Add
                                  </button>
                                </div>

                              </div>

                              {/* Notes toggle */}
                              <button
                                onClick={() =>
                                  setOpenNotes(prev => ({
                                    ...prev,
                                    [op.id]: !prev[op.id]
                                  }))
                                }
                                className="text-orange-600 text-xs mt-2"
                              >
                                {openNotes[op.id] ? 'Hide Notes' : 'Expand Notes'}
                              </button>

                              {openNotes[op.id] && (
                                <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                                  {op.activities.map(a => (
                                    <div key={a.id} className="mb-2 border-b pb-1">

                                      {/* Activity type */}
                                      {a.activity_type && (
                                        <div className="text-[10px] font-semibold text-gray-600">
                                          {a.activity_type}
                                        </div>
                                      )}

                                      {/* Note */}
                                      {a.note && (
                                        <div>
                                          <strong>{a.owner}</strong>: {a.note}
                                        </div>
                                      )}

                                      {/* Next action */}
                                      {a.next_action_date && (
                                        <div className="text-[10px] text-blue-600">
                                          Next: {new Date(a.next_action_date).toLocaleDateString('es-CO')}
                                        </div>
                                      )}

                                      {/* Timestamp */}
                                      <div className="text-gray-400 text-[10px]">
                                        {new Date(a.created_at).toLocaleString('es-CO')}
                                      </div>

                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Hide details */}
                              {openCards[op.id] && (
                                <div className="flex justify-center mt-2">
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenCards(prev => ({
                                        ...prev,
                                        [op.id]: false
                                      }));
                                    }}
                                    className="material-symbols-outlined text-gray-400 text-sm cursor-pointer hover:text-gray-600"
                                  >
                                    drag_handle
                                  </span>
                                </div>
                              )}

                            </div>

                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'list' && (

              <div className="bg-white rounded shadow overflow-hidden">

                {/* Header */}
                <div className="grid grid-cols-7 gap-2 px-3 py-2 text-xs font-semibold text-gray-500 border-b">
                  <div>Client</div>
                  <div>Project</div>
                  <div>Value</div>
                  <div>LOB</div>
                  <div>Owner</div>
                  <div>Probability</div>
                  <div>Status</div>
                </div>

                {/* Rows */}
                {filteredOpportunities.map(op => {

                  const isOverdue =
                    !['won', 'lost'].includes(op.status) &&
                    op.est_close_date &&
                    new Date(op.est_close_date) < new Date();

                  const referenceDate = op.last_activity || op.created_at;

                  const isInactive =
                    !['won', 'lost'].includes(op.status) &&
                    !op.next_action_date &&
                    (new Date() - new Date(referenceDate)) / (1000 * 60 * 60 * 24) > 7;

                  const isActionDue =
                    op.next_action_date &&
                    new Date(op.next_action_date) <= new Date();

                  return (
                    <React.Fragment key={op.id}>

                      {/* MAIN ROW */}
                      <div
                        onClick={() =>
                          setOpenRows(prev => ({
                            ...prev,
                            [op.id]: !prev[op.id]
                          }))
                        }
                        className={`grid grid-cols-7 gap-2 px-3 py-2 text-sm border-b items-center hover:bg-gray-50 cursor-pointer
        ${isOverdue ? 'bg-red-50' : isInactive ? 'bg-yellow-50' : ''}`}
                      >

                        <div className="flex items-center gap-2">

                          {/* Chevron */}
                          {openRows[op.id] ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 -960 960 960"
                              className="w-4 h-4 shrink-0"
                              fill="#016690"
                            >
                              <path d="M480-333 240-573l51-51 189 189 189-189 51 51-240 240Z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 -960 960 960"
                              className="w-4 h-4 shrink-0"
                              fill="#016690"
                            >
                              <path d="M522-480 333-669l51-51 240 240-240 240-51-51 189-189Z" />
                            </svg>
                          )}

                          {/* Client */}
                          <span className="truncate">{op.client}</span>

                        </div>
                        <div className="truncate text-gray-600">{op.project}</div>
                        <div>${Number(op.value || 0).toLocaleString('es-CO')}</div>
                        <div className="text-xs text-gray-500">{op.line_of_business || '—'}</div>
                        <div className="text-xs text-gray-500">{op.owner}</div>
                        <div>{op.probability}%</div>
                        <div className="capitalize text-xs">{op.status}</div>

                      </div>

                      {/* EXPANDED ROW */}
                      {openRows[op.id] && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="px-4 py-3 bg-gray-50 border-b"
                        >

                          {/* Top row: meta */}
                          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500 mb-2">

                            <div className="flex gap-4">
                              <div><span className="font-medium text-gray-600">Days Open:</span> {op.days_open}</div>
                              <div><span className="font-medium text-gray-600">Due:</span> {op.est_close_date || '—'}</div>
                            </div>

                            {/* Status highlight */}
                            <div className="capitalize text-[11px] px-2 py-0.5 rounded bg-white border text-gray-600">
                              {op.status}
                            </div>

                          </div>

                          {/* Activity */}
                          <div className="text-sm text-gray-600 mb-3">
                            {op.last_activity_note || 'No recent activity'}
                          </div>

                          {/* Progress */}
                          <div className="flex items-center gap-3">

                            <div className="flex-1 h-2 bg-gray-200 rounded">
                              <div
                                className="h-2 bg-green-600 rounded"
                                style={{ width: `${op.probability}%` }}
                              />
                            </div>

                            <div className="text-xs text-gray-500 w-10 text-right">
                              {op.probability}%
                            </div>

                          </div>

                        </div>
                      )}

                    </React.Fragment>
                  );
                })}

              </div>

            )}

          </div>
        )}

        {view === 'setup' && (
          <div style={{ position: 'relative', zIndex: 9999 }}>

            <h1 className="text-2xl font-semibold mb-6">Setup</h1>

            <div className="grid grid-cols-3 gap-6 relative z-0">

              {/* Clients */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-semibold mb-3">Clients</h3>

                <div className="flex gap-2 mb-3 w-full">
                  <input
                    placeholder="New client"
                    value={form.client_name || ''}
                    onChange={(e) =>
                      setForm({ ...form, client_name: e.target.value })
                    }
                    className="border p-2 rounded w-full min-w-0"
                  />

                  <button
                  title="Add"
                    onClick={() => {
                      if (!form.client_name) return;

                      fetch(`${API}/accounts`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: form.client_name,
                          industry: 'N/A',
                          country: 'N/A',
                          status: 'active'
                        })
                      }).then(() => {
                        fetchAccounts();
                        setForm({ ...form, client_name: '' });
                      });
                    }}
                    className="bg-blue-600 text-white px-3 h-[38px] rounded shrink-0 flex items-center justify-center hover:bg-blue-700 active:scale-95 transition"
                  >
                    <svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 -960 960 960"
  className="w-4 h-4"
  fill="currentColor"
>
  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
</svg>
                  </button>
                </div>

                <ul className="text-sm">
                  {[...accounts]
  .sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )
  .map(a => (
                    <li key={a.id}>{a.name}</li>
                  ))}
                </ul>
              </div>

              {/* Owners */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-semibold mb-3">Owners</h3>

                <div className="flex gap-2 mb-3 w-full">
                  <input
                    placeholder="Name"
                    value={form.owner_name || ''}
                    onChange={(e) =>
                      setForm({ ...form, owner_name: e.target.value })
                    }
                    className="border p-2 rounded w-full min-w-0"
                  />

                  <input
                    placeholder="Email"
                    value={form.owner_email || ''}
                    onChange={(e) =>
                      setForm({ ...form, owner_email: e.target.value })
                    }
                    className="border p-2 rounded w-full min-w-0"
                  />

                  <button
                  title="Add"
                    onClick={() => {
  console.log('CLICK OWNER BUTTON');
                      if (!form.owner_name || !form.owner_email) return;

                      console.log('SENDING →', {
  name: form.owner_name,
  email: form.owner_email
});

                      fetch(`${API}/owners`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
  name: form.owner_name,
  email: form.owner_email,
  password: '123456'   // 👈 add this
})
})


  .then(async (res) => {
    console.log('OWNER POST status:', res.status);

    const data = await res.text();
    console.log('OWNER POST response:', data);

    return fetchOwners();
  })
  .then(() => {
    setForm(prev => ({
      ...prev,
      owner_name: '',
      owner_email: ''
    }));
  })
  .catch(err => {
    console.error('OWNER ERROR:', err);
  });
                    }}
                    className="bg-blue-600 text-white px-3 h-[38px] rounded shrink-0 flex items-center justify-center hover:bg-blue-700 active:scale-95 transition"
                    style={{ pointerEvents: 'auto', position: 'relative', zIndex: 9999 }}
                  >
                    <svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 -960 960 960"
  className="w-4 h-4"
  fill="currentColor"
>
  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
</svg>
                  </button>
                </div>

                <ul className="text-sm overflow-hidden">
                  {[...owners]
  .sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  )
  .map(o => (
                    <li key={o.id}>
                      {o.name} — <span className="text-gray-400">{o.email}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Line of Business */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                <h3 className="font-semibold mb-3">Line of Business</h3>

                <div className="flex gap-2 mb-3 w-full">
                  <input
                    placeholder="New LOB"
                    value={form.lob_name || ''}
                    onChange={(e) =>
                      setForm({ ...form, lob_name: e.target.value })
                    }
                    className="border p-2 rounded w-full min-w-0"
                  />

                  <button
                  title="Add"
                    onClick={() => {
                      if (!form.lob_name) return;

                      fetch(`${API}/lob`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: form.lob_name
                        })
                      })
                        .then(async res => {
                          console.log('LOB POST status:', res.status);

                          const data = await res.text();
                          console.log('LOB POST response:', data);

                          if (!res.ok) throw new Error('LOB create failed');

                          return fetchLobs();
                        })
                        .then(() => {
                          setForm({ ...form, lob_name: '' });
                        })
                        .catch(err => {
                          console.error('LOB ERROR:', err);
                        });
                    }}
                    className="bg-blue-600 text-white px-3 h-[38px] rounded shrink-0 flex items-center justify-center hover:bg-blue-700 active:scale-95 transition"
                  >
                    <svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 -960 960 960"
  className="w-4 h-4"
  fill="currentColor"
>
  <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
</svg>
                  </button>
                </div>

                <ul className="text-sm">
                  {lobs.map(l => (
                    <li key={l.id}>{l.name}</li>
                  ))}
                </ul>
              </div>

            </div>

          </div>
        )}

      </div>
    </div>
  );
}

const Funnel = ({ opportunities, stageTiming }) => {

  const [mode, setMode] = React.useState('value'); // 'value' | 'weighted'

  const stages = ['lead', 'qualified', 'proposal', 'won', 'lost'];

  const data = stages.map(stage => {
    const deals = opportunities.filter(op => op.status === stage);

    const rawValue = deals.reduce(
      (sum, op) => sum + Number(op.value || 0),
      0
    );

    const weightedValue = deals.reduce((sum, op) => {
      const value = Number(op.value || 0);

      if (op.status === 'won') return sum + value;
      if (op.status === 'lost') return sum + value;

      return sum + (value * Number(op.probability || 0) / 100);
    }, 0);

    return {
      stage,
      count: deals.length,
      value: mode === 'value' ? rawValue : weightedValue
    };
  });

  const maxValue = Math.max(...data.map(d => d.value), 1);

  // ✅ Map stage → avg_days
  const timingMap = Object.fromEntries(
    (stageTiming || []).map(s => [s.stage, Number(s.avg_days)])
  );

  return (
    <div className="bg-white p-4 rounded-[20px] shadow">

      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-500">Pipeline Funnel</p>

        {/* Toggle */}
        <div className="flex text-[10px] bg-gray-100 rounded-full overflow-hidden">
          <button
            onClick={() => setMode('value')}
            className={`px-2 py-1 transition-all ${mode === 'value' ? 'bg-[#016690] text-white' : 'text-gray-500'}`}
          >
            Performance
          </button>
          <button
            onClick={() => setMode('weighted')}
            className={`px-2 py-1 transition-all ${mode === 'weighted' ? 'bg-[#016690] text-white' : 'text-gray-500'}`}
          >
            Forecast
          </button>
        </div>
      </div>

      <div className="space-y-3">

        {data.map((d, i) => {

          const widthPct = (d.value / maxValue) * 100;

          let valueConversion = null;
          let countConversion = null;

          if (i > 0) {
            const prev = data[i - 1];

            valueConversion = prev.value
              ? (d.value / prev.value) * 100
              : 0;

            countConversion = prev.count
              ? (d.count / prev.count) * 100
              : 0;
          }

          return (
            <div key={d.stage}>

              {/* Row */}
              <div className="flex justify-between items-center mb-1 text-xs">

                {/* Stage + Time */}
                <span className="capitalize font-medium text-gray-600">
                  {d.stage}
                  {timingMap[d.stage] !== undefined && (
                    <span className="text-gray-400 ml-1">
                      ({Math.round(timingMap[d.stage])}d)
                    </span>
                  )}
                </span>

                {/* Metrics */}
                <div className="flex items-center gap-3 text-gray-500">

                  {/* Deals */}
                  <span>{d.count} deals</span>

                  {/* Conversion */}
                  {i > 0 && (
                    <span className="flex items-center gap-1">

                      <span className="font-semibold text-[#016690]">
                        {Math.round(valueConversion)}%
                      </span>

                      <span className="text-gray-400">
                        ({Math.round(countConversion)}%)
                      </span>

                    </span>
                  )}

                  {/* Value */}
                  <span
                    className={`font-semibold ${d.stage === 'won'
                      ? 'text-[#016690]'
                      : d.stage === 'lost'
                        ? 'text-[#d97706]'
                        : 'text-gray-700'
                      }`}
                  >
                    ${Math.round(d.value).toLocaleString('es-CO')}
                  </span>

                </div>
              </div>

              {/* Bar */}
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-2 rounded-full transition-all duration-700 ease-out"
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor:
                      d.stage === 'won'
                        ? '#016690'
                        : d.stage === 'lost'
                          ? '#d97706'
                          : '#7bc861'
                  }}
                />
              </div>

            </div>
          );
        })}

      </div>

      {/* Legend */}
      <div className="text-[10px] text-gray-400 mt-3 text-right">
        Value % vs previous stage | (Deal %)
      </div>

    </div>
  );
};

export default App;